/**
 * The ledger's URL-carrying context (P4-T2).
 *
 * Account, month, row mode, search, filters, sort and page live in the query string, and
 * that is the only place they live: the UI's own state is derived from `location.search`,
 * so a view is deep-linkable, browser back/forward is honest (each navigation is a real
 * history entry, and `popstate` re-reads the same source of truth), and the editor can be
 * opened and closed without losing the context underneath it.
 *
 * This module is pure — it takes a query string and returns a value, or the reverse. It
 * touches neither `window` nor `history`, which is what lets the rules below be asserted
 * directly in the fast suite instead of inferred from a browser journey.
 *
 * A malformed value is *replaced by its default* rather than accepted: an unknown UUID, a
 * `month=2026-13`, a `page=0` or an unrecognised `sort` yields a usable view instead of an
 * error page, and never a silently different filter.
 */

import { isMonth } from './calendar';

export type RowMode = 'due' | 'transaction';
export type RowType = 'all' | 'income' | 'expense' | 'opening' | 'transfer';
export type RowState = 'all' | 'effective' | 'pending';
export type RowSort = 'date-asc' | 'date-desc' | 'amount-asc' | 'amount-desc';

/** Every context field with its default, in one place. */
export const CONTEXT_DEFAULTS = {
  account: null,
  mode: 'due',
  query: '',
  type: 'all',
  state: 'all',
  sort: 'date-asc',
  page: 1,
  canceled: false,
} as const;

export interface LedgerContext {
  /** The selected account id (a lowercase UUID), or `null` for the directory. */
  readonly account: string | null;
  readonly month: string;
  readonly mode: RowMode;
  readonly query: string;
  readonly type: RowType;
  readonly state: RowState;
  readonly sort: RowSort;
  /** 1-based. */
  readonly page: number;
  /**
   * Whether canceled rows are asked for (P7-T1).
   *
   * It is context rather than a component's private state for the same reason every other filter
   * is: it changes what the server returns (`includeCanceled`), so a view that shows canceled rows
   * has to be linkable to — and a canceled row has to be reachable at all, because cancellation is
   * reversible and restoring it means seeing it.
   */
  readonly canceled: boolean;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const MODES: readonly RowMode[] = ['due', 'transaction'];
const TYPES: readonly RowType[] = ['all', 'income', 'expense', 'opening', 'transfer'];
const STATES: readonly RowState[] = ['all', 'effective', 'pending'];
const SORTS: readonly RowSort[] = ['date-asc', 'date-desc', 'amount-asc', 'amount-desc'];

/** The longest search term the URL will carry; longer input is truncated, not rejected. */
export const MAX_QUERY_LENGTH = 120;

function one(value: string | null): string | undefined {
  return value === null || value === '' ? undefined : value;
}

function pick<T extends string>(value: string | undefined, allowed: readonly T[], fallback: T): T {
  return value !== undefined && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

/**
 * Read the context out of a query string.
 *
 * `fallbackMonth` is the business month to open on when the URL names none — the caller
 * derives it from `@saldo/domain`'s `todayInTimeZone`, so an unaddressed visit opens on the
 * business "now" rather than on the browser's own month.
 */
export function readContext(search: string, fallbackMonth: string): LedgerContext {
  const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  const account = one(params.get('account'));
  const month = one(params.get('month'));
  const pageValue = Number(one(params.get('page')) ?? '1');
  return {
    account: account !== undefined && UUID.test(account) ? account : null,
    month: month !== undefined && isMonth(month) ? month : fallbackMonth,
    mode: pick(one(params.get('mode')), MODES, CONTEXT_DEFAULTS.mode),
    query: (one(params.get('q')) ?? '').slice(0, MAX_QUERY_LENGTH),
    type: pick(one(params.get('type')), TYPES, CONTEXT_DEFAULTS.type),
    state: pick(one(params.get('state')), STATES, CONTEXT_DEFAULTS.state),
    sort: pick(one(params.get('sort')), SORTS, CONTEXT_DEFAULTS.sort),
    page: Number.isInteger(pageValue) && pageValue >= 1 ? pageValue : CONTEXT_DEFAULTS.page,
    canceled: one(params.get('canceled')) === 'true',
  };
}

/**
 * The canonical query string for a context: a fixed parameter order, and defaults omitted.
 *
 * Canonical, so two contexts that mean the same thing write the same URL — which is what
 * makes a history entry comparable, and what keeps a reload from producing a different
 * address than the one the visit already had.
 */
export function searchFor(context: LedgerContext): string {
  const params = new URLSearchParams();
  if (context.account !== null) params.set('account', context.account);
  params.set('month', context.month);
  if (context.mode !== CONTEXT_DEFAULTS.mode) params.set('mode', context.mode);
  if (context.query !== '') params.set('q', context.query);
  if (context.type !== CONTEXT_DEFAULTS.type) params.set('type', context.type);
  if (context.state !== CONTEXT_DEFAULTS.state) params.set('state', context.state);
  if (context.sort !== CONTEXT_DEFAULTS.sort) params.set('sort', context.sort);
  if (context.page > 1) params.set('page', String(context.page));
  if (context.canceled) params.set('canceled', 'true');
  return `?${params.toString()}`;
}

/** The address bar value for a context. */
export function urlFor(context: LedgerContext): string {
  return `/${searchFor(context)}`;
}

/** Whether two contexts select the same view. */
export function contextEquals(left: LedgerContext, right: LedgerContext): boolean {
  return (
    left.account === right.account &&
    left.month === right.month &&
    left.mode === right.mode &&
    left.query === right.query &&
    left.type === right.type &&
    left.state === right.state &&
    left.sort === right.sort &&
    left.page === right.page &&
    left.canceled === right.canceled
  );
}

/**
 * Apply a change to a context.
 *
 * Any change other than paging resets to page 1: a different month or filter addresses a
 * different list, and keeping the old page number there is how a view appears empty while
 * rows exist.
 */
export function withChange(context: LedgerContext, patch: Partial<LedgerContext>): LedgerContext {
  const next: LedgerContext = { ...context, ...patch };
  const pagedItself = patch.page !== undefined && Object.keys(patch).length === 1;
  return pagedItself ? next : { ...next, page: patch.page ?? 1 };
}

/** Whether any secondary row filter (search, type, state) is active. */
export function hasRowFilters(context: LedgerContext): boolean {
  return context.query !== '' || context.type !== 'all' || context.state !== 'all';
}
